// components/designer/CanvasArea.tsx
/**
 * Canvas Area Component
 *
 * Main canvas rendering area that displays the page with components.
 * Handles grid display, empty state, and component iteration.
 */

import { Info, Layout } from "lucide-react";
import React from "react";
import { NormalizedComponent } from "../../types";
import CanvasComponent from "../designer/CanvasComponent";

interface CanvasAreaProps {
  components: NormalizedComponent[];
  selectedComponent: string | null;
  pageSize: { width: number; height: number };
  showGrid: boolean;
  tool: "select" | "move";
  onCanvasClick: (e: React.MouseEvent) => void;
  onComponentClick: (e: React.MouseEvent, id: string) => void;
  onComponentMouseDown: (
    e: React.MouseEvent,
    id: string,
    isResize?: boolean,
    handle?: string
  ) => void;
  onContextMenu: (e: React.MouseEvent, id: string) => void;
  renderComponent: (id: string, data: any) => string;
  mockData: any;
  onUpdateComponent: (
    id: string,
    updates: Partial<NormalizedComponent>
  ) => void;
  onDeleteComponent: (id: string) => void;
  onDuplicateComponent: (id: string) => void;
  canvasRef: React.RefObject<HTMLDivElement>;
  isInitialized: boolean;
  perforationEnabled?: boolean;
  perforationY?: number;
  isDoubleSided?: boolean;
  activeSide?: "front" | "back";
}

const CanvasArea: React.FC<CanvasAreaProps> = ({
  components,
  selectedComponent,
  pageSize,
  showGrid,
  tool,
  onCanvasClick,
  onComponentClick,
  onComponentMouseDown,
  onContextMenu,
  renderComponent,
  mockData,
  onUpdateComponent,
  onDeleteComponent,
  onDuplicateComponent,
  canvasRef,
  isInitialized,
  perforationEnabled = false,
  perforationY = 0,
  isDoubleSided = false,
  activeSide,
}) => {
  return (
    <div className="h-full bg-gray-100 overflow-auto">
      <div className="p-3">
        {isDoubleSided && (
          <div className="flex justify-center mb-2">
            <div className="inline-flex items-center gap-1.5 rounded-md border border-blue-200 bg-blue-50 px-2 py-1 text-blue-700">
              <Info className="h-3.5 w-3.5 text-blue-600" />
              <span className="text-xs leading-none">
                You are editing the <strong>{activeSide} page</strong> of the
                Template.
              </span>
            </div>
          </div>
        )}
        <div
          ref={canvasRef}
          className="relative mx-auto bg-white shadow-lg"
          style={{
            width: pageSize.width,
            height: pageSize.height,
            backgroundImage: showGrid
              ? "radial-gradient(circle, #ddd 1px, transparent 1px)"
              : "none",
            backgroundSize: showGrid ? "10px 10px" : "auto",
            cursor: tool === "move" ? "move" : "crosshair",
          }}
          onClick={onCanvasClick}
        >
          {/* Perforation Line Rendering */}
          {perforationEnabled && (
            <div
              className="absolute left-0 w-full border-t-2 border-dashed border-black pointer-events-none z-50 flex items-center justify-center"
              style={{ top: perforationY }}
            >
              <span className="block w-full border-dashed border-black"></span>
            </div>
          )}
          {components.length === 0 && (
            <div className="absolute inset-0 flex items-center justify-center">
              <div className="text-center text-gray-400">
                <Layout size={48} className="mx-auto mb-4 opacity-50" />
                <p className="text-lg">Canvas is empty</p>
                <p className="text-sm">Add components from left panel</p>
              </div>
            </div>
          )}

          {components.map((component) => {
            // Get fresh rendered HTML every time
            const renderedHtml = isInitialized
              ? renderComponent(component.id, mockData)
              : "";

            return (
              <CanvasComponent
                key={component.id}
                component={component}
                isSelected={selectedComponent === component.id}
                tool={tool}
                renderedHtml={renderedHtml}
                onClick={(e) => onComponentClick(e, component.id)}
                onMouseDown={(e, isResize, handle) =>
                  onComponentMouseDown(e, component.id, isResize, handle)
                }
                onContextMenu={(e) => onContextMenu(e, component.id)}
                onUpdate={(updates) => onUpdateComponent(component.id, updates)}
                onDelete={() => onDeleteComponent(component.id)}
                onDuplicate={() => onDuplicateComponent(component.id)}
              />
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default CanvasArea;
